import { FormControl, MenuItem, Select } from '@material-ui/core';
import TextCheckButton from 'components/common/button/TextCheckButton';
import CustomCheckbox from 'components/common/checkbox/CustomCheckbox';
import MuiWrapper from 'components/common/input/MuiWrapper';
import UnitSlider from 'components/common/slider/UnitSlider';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import { indicationOptionList } from 'lib/mapper';
import { IToothShade } from 'lib/types/projectTypes';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface BasicOptionProps {
  defaultOption: {
    inputs: {
      [key: string]: any;
    };
    onChange: (event: any) => void;
    setInputs: React.Dispatch<React.SetStateAction<{}>>;
    onReset: () => void;
  };
  defaultValue: {
    inputs: {
      [key: string]: any;
    };
    onChange: (event: any) => void;
    setInputs: React.Dispatch<React.SetStateAction<{}>>;
    onReset: () => void;
  };
  isBeElement: {
    checkBox: boolean;
    radioButton: boolean;
    slider: boolean;
  };
  matchingImplantList: any;
  matchingAbutmentList: any;
  matchingScanbodyList: any;
  materialShadeList: IToothShade[];
  restorationPontic: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  occlusalSurface: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  ceramicMetal: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  indicationSeq: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  indicationOption: any;
  implant: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  abutment: {
    value: number | null;
    onChange: (e: any) => void;
    setValue: (value: number | null) => void;
  };
  scanbody: {
    value: number | null;
    onChange: (e: any) => void;
    setValue: (value: number | null) => void;
  };
  checkSituScan: {
    value: boolean | number;
    onChange: (e: any) => void;
    setValue: (value: boolean | number) => void;
  };
  checkGingivaScan: {
    value: boolean | number;
    onChange: (e: any) => void;
    setValue: (value: boolean | number) => void;
  };
  gapWidthCement: {
    value: number | number[] | string | any; // input value 입력시 string
    onChange: (e: any) => void;
    setValue: (value: number | number[] | string | any) => void;
  };
  minimalTickness: {
    value: number | number[] | string | any; // input value 입력시 string
    onChange: (e: any) => void;
    setValue: (value: number | number[] | string | any) => void;
  };
  millingToolDiameter: {
    value: number | number[] | string | any; // input value 입력시 string
    onChange: (e: any) => void;
    setValue: (value: number | number[] | string | any) => void;
  };
  virtualGingiva: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  thimbleCrown: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  waxupScan: {
    value?: number | null;
    onChange: (e: any) => void;
    setValue: (value?: number | null) => void;
  };
  cervicalAdaption: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  materialShade: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  onChooseImplant: (seq: number) => void;
  onChooseAbutment: (seq: number) => void;
  onChooseScanbody: (seq: number) => void;
}

function BasicOption({
  defaultOption,
  defaultValue,
  isBeElement,
  indicationSeq,
  implant,
  abutment,
  scanbody,
  checkSituScan,
  checkGingivaScan,
  gapWidthCement,
  minimalTickness,
  millingToolDiameter,
  virtualGingiva,
  thimbleCrown,
  waxupScan,
  cervicalAdaption,
  materialShade,
  restorationPontic,
  occlusalSurface,
  ceramicMetal,
  matchingImplantList,
  matchingAbutmentList,
  matchingScanbodyList,
  materialShadeList,
  onChooseImplant,
  onChooseAbutment,
  onChooseScanbody,
}: BasicOptionProps) {
  const { t } = useTranslation();

  return (
    <StyledBasicOption>
      <div className="basicOption__select_option">
        {/* Scan Abutment */}
        {!!matchingImplantList?.length && (
          <div className="basicOption__select_item">
            <div className="select_option_title">
              <CustomText className="select_option_title" fontSize={14} fontWeight={500}>
                <T>PROJECT_SCAN_ABUTMENT</T>
              </CustomText>
            </div>
            <MuiWrapper className="select_option_content sm" isGlobalStyle>
              <FormControl fullWidth variant="outlined">
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    marginThreshold: 10,
                  }}
                  displayEmpty
                  name="implant"
                  value={implant.value}
                  onChange={(e: any) => {
                    onChooseImplant(e.target.value);
                  }}
                >
                  {matchingImplantList?.length > 0 &&
                    matchingImplantList.map((partItem: any, partIdx: number) => {
                      return (
                        <MenuItem key={partIdx} value={partItem.seq}>
                          {t(`ABUTMENT_IMPLANT_${partItem.alias}`)}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </MuiWrapper>
          </div>
        )}
        {/* Abutment Material */}
        {implant?.value === 8 && (
          <div className="basicOption__select_item sub_content">
            <div className="select_option_title sub_content">
              <CustomText fontSize={14} fontWeight={500}>
                <T>PROJECT_IMPLANT_ABUTMENT</T>
              </CustomText>
            </div>
            <MuiWrapper className="select_option_content sm" isGlobalStyle>
              <FormControl fullWidth variant="outlined">
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    marginThreshold: 10,
                  }}
                  displayEmpty
                  name="abutment"
                  value={abutment.value}
                  onChange={(e: any) => {
                    onChooseAbutment(e.target.value);
                  }}
                >
                  {!!matchingAbutmentList?.length &&
                    matchingAbutmentList.map((partItem: any, partIdx: number) => {
                      return (
                        <MenuItem key={partIdx} value={partItem.seq}>
                          {t(`ABUTMENT_MATERIAL_${partItem.seq}`)}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </MuiWrapper>
          </div>
        )}
        {/* Scan Scanbody */}
        {(implant?.value === 8 || implant?.value === 9) && (
          <div className="basicOption__select_item sub_content">
            <div className="select_option_title sub_content">
              <CustomText fontSize={14} fontWeight={500}>
                <T>PROJECT_SCAN_SCANBODY</T>
              </CustomText>
            </div>

            <MuiWrapper className="select_option_content sm" isGlobalStyle>
              <FormControl fullWidth variant="outlined">
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    marginThreshold: 10,
                  }}
                  displayEmpty
                  name="scanbody"
                  value={scanbody.value}
                  onChange={(e: any) => {
                    onChooseScanbody(e.target.value);
                  }}
                >
                  {!!matchingScanbodyList?.length &&
                    matchingScanbodyList.map((partItem: any, partIdx: number) => {
                      return (
                        <MenuItem key={partIdx} value={partItem.seq}>
                          {t(`ABUTMENT_SCANBODY_${partItem.seq}`)}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </MuiWrapper>
          </div>
        )}

        {/* Main Option */}
        {useMemo(() => {
          return indicationOptionList.default.mainOption.map((option: any, index: number) => {
            if (!defaultOption?.inputs || !defaultValue?.inputs) return;

            const optionItem = defaultOption?.inputs[option.key];
            const optionValue = defaultValue?.inputs[option.key];

            if (!!optionItem) {
              return option.type === 'radioButton' ? (
                <div className="basicOption__select_item" key={index}>
                  <div className="select_option_title">
                    <CustomText fontSize={14} fontWeight={500}>
                      {option.title}
                    </CustomText>
                  </div>

                  {optionItem.values.map((valueItem: any, index: number) => (
                    <div className="select_option_content" key={index}>
                      <TextCheckButton
                        className="form__text_check_btn"
                        active={optionValue === valueItem?.value}
                        width="100%"
                        onClick={() => {
                          defaultValue.setInputs({
                            ...defaultValue.inputs,
                            [option.key]: valueItem?.value,
                          });
                        }}
                      >
                        {valueItem?.description}
                      </TextCheckButton>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="basicOption__select_item" key={index}>
                  <div className="select_option_title">
                    <CustomText className="select_option_title" fontSize={14} fontWeight={500}>
                      {option.title}
                    </CustomText>
                  </div>
                  <MuiWrapper className="select_option_content sm" isGlobalStyle>
                    <FormControl fullWidth variant="outlined">
                      <Select
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                          marginThreshold: 10,
                        }}
                        displayEmpty
                        name={option.key}
                        value={optionValue}
                        onChange={(e: any) => {
                          defaultValue.setInputs({
                            ...defaultValue.inputs,
                            [option.key]: e.target.value,
                          });
                        }}
                      >
                        {optionItem.values.map((valueItem: any, index: number) => (
                          <MenuItem key={index} value={valueItem.value}>
                            {valueItem.description}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </MuiWrapper>
                </div>
              );
            }
          });
        }, [indicationOptionList, defaultOption, defaultValue])}

        {/* Default Option */}
        {useMemo(() => {
          if (!isBeElement.checkBox && !isBeElement.radioButton && !isBeElement.slider) return;

          return (
            <div className="basicOption__option_box">
              {/* Check Box */}
              {isBeElement.checkBox && (
                <div className="basicOption__item_box scan_type">
                  <div className="scan_type_option">
                    {indicationOptionList.default.checkBox.map((option: any, index: number) => {
                      if (!defaultOption?.inputs || !defaultValue?.inputs) return;

                      const optionItem = defaultOption?.inputs[option.key];
                      const optionValue = defaultValue?.inputs[option.key];

                      if (!!optionItem) {
                        return (
                          <div className="scan_type_option_item" key={index}>
                            <label className="form__option_checkbox_label cursor-pointer">
                              <CustomCheckbox
                                checked={!!optionValue}
                                onChange={() => {
                                  defaultValue.setInputs({
                                    ...defaultValue.inputs,
                                    [option.key]: optionValue === 1 ? 0 : 1,
                                  });
                                }}
                                marginRight={12}
                                width={18}
                              />
                              <CustomText fontSize={13} fontColor="#444" fontWeight={400}>
                                {option.title}
                              </CustomText>
                            </label>
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
              {/* Radio Button */}
              {isBeElement.radioButton && (
                <div className="basicOption__item_box">
                  <div className="radio_option">
                    {indicationOptionList.default.radioButton.map((option: any, index: number) => {
                      if (!defaultOption?.inputs || !defaultValue?.inputs) return;

                      const optionItem = defaultOption.inputs[option.key];
                      const optionValue = defaultValue.inputs[option.key];

                      if (!!optionItem) {
                        return (
                          <div className="radio_option_item" key={index}>
                            <CustomText
                              className="option_title"
                              fontSize={13}
                              fontColor="#444"
                              fontWeight={400}
                            >
                              - {option.title}
                            </CustomText>

                            {optionItem.values.map((valueItem: any, index: number) => (
                              <TextCheckButton
                                className="form__text_check_btn"
                                active={optionValue === valueItem?.value}
                                width="148px"
                                padding="0 10px"
                                minHeight="30px"
                                key={index}
                                onClick={() => {
                                  defaultValue.setInputs({
                                    ...defaultValue.inputs,
                                    [option.key]: valueItem?.value,
                                  });
                                }}
                              >
                                <CustomText
                                  fontSize={13}
                                  fontWeight={400}
                                  className="option_description"
                                >
                                  {valueItem?.description}
                                </CustomText>
                              </TextCheckButton>
                            ))}
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
              {/* Slider */}
              {isBeElement.slider && (
                <div className="basicOption__item_box">
                  <div className="slider_box">
                    {indicationOptionList.default.slider.map((option: any, index: number) => {
                      if (!defaultOption?.inputs || !defaultValue?.inputs) return;

                      const optionItem = defaultOption.inputs[option.key];
                      const optionValue = defaultValue.inputs[option.key];

                      if (!!optionItem && (optionValue === 0 || !!optionValue)) {
                        return (
                          <div className="slider_option" key={index}>
                            <CustomText
                              fontSize={13}
                              fontWeight={400}
                              fontColor="#444"
                              className="slider_option_label"
                            >
                              {option.title}
                            </CustomText>
                            <UnitSlider
                              ariaLabelledby={`${option.key}-slider`}
                              data={optionValue}
                              min={optionItem?.minValue}
                              max={optionItem?.maxValue}
                              onValueChange={newVal => {
                                defaultValue.setInputs({
                                  ...defaultValue.inputs,
                                  [option.key]: newVal,
                                });
                              }}
                              step={optionItem?.stepSize}
                              digit={6}
                            />
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        }, [indicationOptionList, defaultOption, defaultValue, isBeElement])}
        {/* Material Shade */}
        <div className="basicOption__select_item">
          <div className="select_option_title">
            <CustomText fontSize={13} fontWeight={400} fontColor="#444">
              <T>PROJECT_MATERIAL_SHADE</T>
            </CustomText>
          </div>
          <MuiWrapper className="projectIndication__material_shade_box sm" isGlobalStyle>
            <FormControl fullWidth variant="outlined">
              <Select
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  marginThreshold: 10,
                }}
                displayEmpty
                name="materialShade"
                value={materialShade.value}
                // onChange={materialShade.onChange}
                onChange={(e: any) => {
                  materialShade.setValue(e.target.value);
                }}
              >
                {materialShadeList?.length > 0 &&
                  materialShadeList.map((item: IToothShade, index: number) => {
                    if (!item.group) {
                      return (
                        <MenuItem key={index} value={item.seq}>
                          {item.name}
                        </MenuItem>
                      );
                    }
                  })}
              </Select>
            </FormControl>
          </MuiWrapper>
        </div>
      </div>
    </StyledBasicOption>
  );
}

const StyledBasicOption = styled.div`
  padding: 22px 0;
  overflow: auto;
  .basicOption__select_option {
    display: flex;
    flex-direction: column;
    row-gap: 22px;
    .basicOption__select_item {
      display: flex;
      align-items: center;
      .select_option_title {
        width: 136px;
        &.sub_content {
          width: 200px;
        }
      }
      .select_option_content {
        display: flex;
        flex-grow: 1;
      }
      &.sub_content {
        padding-left: 136px;
      }
    }
    .basicOption__option_box {
      border: 1px solid #e1e1e7;
      border-radius: 5px;
      width: 100%;
      .basicOption__item_box {
        padding: 20px;
        &.scan_type {
          padding: 14px 20px;
        }
        &:not(:last-child) {
          border-bottom: 1px solid #e1e1e7;
        }
        .scan_type_option {
          display: flex;
          column-gap: 46px;
          .scan_type_option_item {
            display: flex;
            align-items: center;
            .form__option_checkbox_label {
              display: flex;
              align-items: center;
            }
          }
        }
        .radio_option {
          display: flex;
          flex-direction: column;
          row-gap: 16px;
          .radio_option_item {
            display: flex;
            align-items: center;
            .option_title {
              width: 173px;
              flex-shrink: 0;
              word-wrap: break-word;
            }
            .option_description {
              text-align: center;
            }
          }
        }
        .slider_box {
          display: flex;
          flex-direction: column;
          row-gap: 5px;
          .slider_option {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            /* margin-top: 15px;
            padding: 8px 0; */
            .slider_option_label {
              width: 100%;
              /* margin-bottom: 5px; */
            }
          }
        }
      }
    }
    .basicOption__design_option {
      display: flex;
      align-items: center;
      .design_option_title {
        width: 150px;
      }
      .design_icon_box {
        display: inline-flex;
        flex-wrap: wrap;
        padding-left: 20px;
        width: calc(100% - 160px);
        .design_icon_radio_label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
          width: 37px;
          height: 37px;
          /* border: 1px solid #bbb; */
          border: 1px solid transparent;
          box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);
          border-radius: 5px;
          text-align: center;
          cursor: pointer;
          user-select: none;

          &:not(:first-child) {
            margin-left: 12px;
          }
          &.active {
            background-color: #e6e6e6;
            border-color: ${color.blue};
          }
          &.pontic {
            align-items: flex-end;
            img {
              height: 82%;
            }
          }
          &.ceramic img {
            height: 65%;
          }
          &.occlusal img {
            width: 75%;
          }
          .tooth_design_input {
            position: absolute;
            top: 0;
            left: 0;
            height: 0;
            width: 0;
            overflow: hidden;
          }
        }
      }
    }
  }
`;

const StyledMidLine = styled.div`
  width: 100%;
  border-bottom: 1px solid #e1e1e7;
`;

export default BasicOption;
